/**
 * Page chrome shared by every routed page: the Back button at the start of the header's
 * breadcrumb bar (PageReturn) and the page-action rail on the right edge (PageRail).
 *
 * The header lives in MainLayout, outside the page, so a page tells it where "return" goes
 * through this context instead of drawing its own Back button:
 *
 *   usePageReturn("/batches")                       // a fixed target
 *   usePageReturn(() => { dispatch(x()); navigate(-1); })  // a target with side effects
 *   usePageReturn(null)                             // hide the icon on this page
 *
 * A page that says nothing goes to its logical parent (menuParent: the menu item it sits under),
 * else history back. The target is reset when the page
 * unmounts, so it never leaks onto the next route.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const DEFAULT_RETURN = -1;

const PageChromeContext = createContext({
  returnTo: DEFAULT_RETURN,
  setReturnTo: () => {},
  railCount: 0,
  registerRail: () => () => {},
  crumbTail: '',
  setCrumbTail: () => {},
  headerBack: undefined,
  setHeaderBack: () => {},
  returnHosts: 0,
  registerReturnHost: () => () => {},
});

export function PageChromeProvider({ children }) {
  const [returnTo, setReturnToState] = useState(DEFAULT_RETURN);
  const [railCount, setRailCount] = useState(0);
  const [crumbTail, setCrumbTail] = useState('');
  const [headerBack, setHeaderBackState] = useState(undefined);
  // a page header that draws the Back button itself (PageReturn placement="inline"); the top bar's then stands down
  const [returnHosts, setReturnHosts] = useState(0);

  // Function targets are stored wrapped so React does not call them as an updater.
  const setReturnTo = useCallback((target) => setReturnToState(() => target), []);
  const setHeaderBack = useCallback((target) => setHeaderBackState(() => target), []);
  const registerRail = useCallback(() => {
    setRailCount((n) => n + 1);
    return () => setRailCount((n) => Math.max(0, n - 1));
  }, []);

  const registerReturnHost = useCallback(() => {
    setReturnHosts((n) => n + 1);
    return () => setReturnHosts((n) => Math.max(0, n - 1));
  }, []);

  const value = useMemo(
    () => ({ returnTo, setReturnTo, railCount, registerRail, crumbTail, setCrumbTail, headerBack, setHeaderBack, returnHosts, registerReturnHost }),
    [returnTo, setReturnTo, railCount, registerRail, crumbTail, headerBack, setHeaderBack, returnHosts, registerReturnHost]
  );
  return <PageChromeContext.Provider value={value}>{children}</PageChromeContext.Provider>;
}

export const usePageChrome = () => useContext(PageChromeContext);

/**
 * Declare where the header's return icon goes on this page.
 * `target`: a path string, a number for history (-1), `{ to, state, replace }`, a function
 * (called on click; does its own navigation), or `null` to hide the icon.
 */
export function usePageReturn(target) {
  const { setReturnTo } = usePageChrome();
  // Keep the latest function without re-registering on every render.
  const latest = useRef(target);
  latest.current = target;
  const isFn = typeof target === 'function';
  const key = isFn ? 'fn' : target === undefined ? 'unset' : JSON.stringify(target);

  useEffect(() => {
    if (target === undefined) return undefined; // nothing declared: leave the default (history back)
    if (isFn) setReturnTo((...args) => latest.current?.(...args));
    else setReturnTo(target);
    return () => setReturnTo(DEFAULT_RETURN);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, setReturnTo]);
}

/**
 * Add a last crumb to the header's breadcrumb for this page (e.g. the open tab: Home / Dashboards / Control Tower / Overview).
 * `label` is one word, or a trail of linked crumbs `[{ label, to }]` (the last is the current page). A page that does
 * not call this is unaffected; the crumb is cleared when the page unmounts.
 */
export function usePageCrumbTail(label) {
  const { setCrumbTail } = usePageChrome();
  const latest = useRef(label);
  latest.current = label;
  // a trail is compared by its content, so a page may pass a fresh array on every render
  const key = Array.isArray(label) ? JSON.stringify(label.map((c) => [c?.label, c?.to])) : label || '';
  useEffect(() => {
    const value = latest.current;
    setCrumbTail(Array.isArray(value) ? value.filter((c) => c?.label) : value || '');
    return () => setCrumbTail('');
  }, [key, setCrumbTail]);
}

/**
 * A page header's own Back target (components/PageHeader `back="/path"` or `back={fn}`), handed to the bar's Back
 * button so the page never draws a second one. It wins over usePageReturn while the header is mounted, because it
 * is the most specific (e.g. a sub-screen inside one route). `undefined` declares nothing.
 */
export function usePageHeaderBack(target) {
  const { setHeaderBack } = usePageChrome();
  const latest = useRef(target);
  latest.current = target;
  const isFn = typeof target === 'function';
  const key = isFn ? 'fn' : typeof target === 'string' ? target : 'none';

  useEffect(() => {
    if (key === 'none') return undefined;
    if (isFn) setHeaderBack((...args) => latest.current?.(...args));
    else setHeaderBack(target);
    return () => setHeaderBack(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, setHeaderBack]);
}
